"use client";

import { useState } from "react";
import { Paper, Text, TextInput, Button, Group, Stack } from "@mantine/core";
import { IconMailHeart, IconCircleCheck } from "@tabler/icons-react";
import { usePathname } from "next/navigation";
import { trackEmailCapture } from "@/lib/analytics";

type Props = {
  /** Where the capture happened, e.g. "color-analysis-quiz" */
  source: string;
  /** Optional quiz result context — used to personalize drip emails */
  season?: string;
  subSeason?: string;
  undertone?: string;
  /** Human-readable result, e.g. "True Summer" or "Warm undertone" */
  resultLabel?: string;
  /** Link back to their result/palette page, included in the email */
  resultUrl?: string;
  /** Override the headline for non-quiz placements */
  title?: string;
  /** Override the sub-copy for non-quiz placements */
  subtitle?: string;
};

export default function EmailResultsCapture({ source, season, subSeason, undertone, resultLabel, resultUrl, title, subtitle }: Props) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");
  const pathname = usePathname();

  const handleSubmit = async () => {
    if (!email.includes("@") || state === "loading") return;
    setState("loading");
    try {
      const locale = pathname?.split("/")[1]?.length === 2 ? pathname.split("/")[1] : "en";
      const res = await fetch("/api/quiz-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source, season, subSeason, undertone, resultLabel, resultUrl, locale }),
      });
      if (!res.ok) throw new Error("failed");
      trackEmailCapture(source);
      setState("done");
    } catch {
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <Paper radius="lg" p="lg" style={{ background: "#faf7f2", borderTop: "3px solid #141414" }}>
        <Group gap="sm" wrap="nowrap">
          <IconCircleCheck size={22} color="#16a34a" style={{ flexShrink: 0 }} />
          <Text size="sm" c="dark.5">
            Check your inbox — your results are on the way. (Look in spam/promotions if you don&apos;t see it.)
          </Text>
        </Group>
      </Paper>
    );
  }

  return (
    <Paper radius="lg" p="lg" style={{ background: "#faf7f2", borderTop: "3px solid #141414" }}>
      <Stack gap={8}>
        <Text fz={10} tt="uppercase" c="dimmed" style={{ letterSpacing: 2 }}>
          Free &nbsp;·&nbsp; Straight to your inbox
        </Text>
        <Group gap="xs" wrap="nowrap">
          <IconMailHeart size={20} color="var(--mantine-color-violet-6)" style={{ flexShrink: 0 }} />
          <Text size="md" fw={500} style={{ letterSpacing: -0.2 }}>
            {title || "Email me my results"}
          </Text>
        </Group>
        <Text size="xs" c="dimmed" lh={1.55}>
          {subtitle || `Get your ${resultLabel ? `${resultLabel} ` : ""}results plus your color guide — free. Unsubscribe anytime.`}
        </Text>
        <Group gap="xs" wrap="nowrap">
          <TextInput
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            style={{ flex: 1 }}
            size="sm"
            error={state === "error" ? "Something went wrong — try again" : undefined}
          />
          <Button size="sm" color="violet" onClick={handleSubmit} loading={state === "loading"}>
            Send
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
